import { cn } from '@lib/cn';
import { IconMoon, IconSun, IconSystem } from '@shared/ui';

import { useTheme } from './theme-context';

import type { ThemePreference } from './theme';
import type { ComponentType, SVGProps } from 'react';

/**
 * Three segments, not a two-state toggle.
 *
 * The default is `system`, and a two-state switch cannot express it: the first
 * click would turn "follow my OS" into an explicit `light` or `dark` with no
 * way back short of clearing storage. Since `system` is the state most
 * operators should be in, it has to stay reachable, and the honest way to show
 * three states is three controls.
 *
 * `aria-pressed` rather than a radio group: these are buttons that take effect
 * immediately, not a choice being collected for a form, and a radio group would
 * promise a submit that never comes. The group carries the label so a screen
 * reader announces "Theme, Light, pressed" rather than three orphan buttons.
 */
const OPTIONS: ReadonlyArray<{
    value: ThemePreference;
    label: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
}> = [
    { value: 'system', label: 'System', Icon: IconSystem },
    { value: 'light', label: 'Light', Icon: IconSun },
    { value: 'dark', label: 'Dark', Icon: IconMoon },
];

export function ThemeSwitch() {
    const { preference, setPreference } = useTheme();

    return (
        <div
            role="group"
            aria-label="Theme"
            className="flex items-center gap-0.5 rounded-lg border border-line bg-surface p-0.5"
        >
            {OPTIONS.map(({ value, label, Icon }) => {
                const active = preference === value;
                return (
                    <button
                        key={value}
                        type="button"
                        // The visible label is the icon, so the accessible name
                        // has to come from here. `aria-pressed` then says which
                        // of the three is on without a second live region.
                        aria-label={label}
                        aria-pressed={active}
                        title={label}
                        onClick={() => setPreference(value)}
                        className={cn(
                            'flex size-7 items-center justify-center rounded-md transition-colors',
                            active
                                ? 'bg-accent-surface text-accent-text'
                                : 'text-content-muted hover:bg-surface-sunken hover:text-content',
                        )}
                    >
                        <Icon className="size-4" aria-hidden="true" />
                    </button>
                );
            })}
        </div>
    );
}
